package link.erpango.quip.catalog.sections

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipSectionHeader

@Composable
fun CatalogDimensSection() {
    LazyColumn(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = QuipDimens.padding16, vertical = QuipDimens.padding12),
        verticalArrangement = Arrangement.spacedBy(QuipDimens.padding24),
    ) {
        item { SpacingSection() }
        item { IconSizesSection() }
        item { CornerRadiiSection() }
        item { ComponentHeightsSection() }
        item { ElevationSection() }
        item { Spacer(Modifier.height(QuipDimens.padding24)) }
    }
}

@Composable
private fun SpacingSection() {
    QuipSectionHeader(title = "Padding / Spacing")
    Spacer(Modifier.height(QuipDimens.padding8))
    val tokens = listOf(
        "padding2"  to QuipDimens.padding2,
        "padding4"  to QuipDimens.padding4,
        "padding6"  to QuipDimens.padding6,
        "padding8"  to QuipDimens.padding8,
        "padding10" to QuipDimens.padding10,
        "padding12" to QuipDimens.padding12,
        "padding16" to QuipDimens.padding16,
        "padding20" to QuipDimens.padding20,
        "padding24" to QuipDimens.padding24,
    )
    Column(verticalArrangement = Arrangement.spacedBy(QuipDimens.padding6)) {
        tokens.forEach { (label, value) ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "$label (${value.value.toInt()}dp)",
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                    modifier = Modifier.width(130.dp),
                )
                Box(
                    modifier = Modifier
                        .width(value * 3)
                        .height(16.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(QuipColors.colorPrimary.copy(alpha = 0.4f)),
                )
            }
        }
    }
}

@Composable
private fun IconSizesSection() {
    QuipSectionHeader(title = "Tamaños de icono")
    Spacer(Modifier.height(QuipDimens.padding8))
    val sizes = listOf(
        "iconSize16" to QuipDimens.iconSize16,
        "iconSize20" to QuipDimens.iconSize20,
        "iconSize24" to QuipDimens.iconSize24,
        "iconSize28" to QuipDimens.iconSize28,
        "iconSize48" to QuipDimens.iconSize48,
    )
    Row(horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding16)) {
        sizes.forEach { (label, size) ->
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Box(
                    modifier = Modifier
                        .width(size)
                        .height(size)
                        .clip(RoundedCornerShape(4.dp))
                        .background(QuipColors.colorPrimary.copy(alpha = 0.25f))
                        .border(1.dp, QuipColors.colorPrimary, RoundedCornerShape(4.dp)),
                )
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "${size.value.toInt()}dp",
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                )
            }
        }
    }
}

@Composable
private fun CornerRadiiSection() {
    QuipSectionHeader(title = "Corner Radius")
    Spacer(Modifier.height(QuipDimens.padding8))
    val radii = listOf(
        "cornerRadius8  (chips, campos)" to QuipDimens.cornerRadius8,
        "cornerRadius12 (cards)"         to QuipDimens.cornerRadius12,
        "cornerRadius16 (modales, FAB)"  to QuipDimens.cornerRadius16,
    )
    Row(horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding16)) {
        radii.forEachIndexed { _, (label, radius) ->
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Box(
                    modifier = Modifier
                        .width(56.dp)
                        .height(40.dp)
                        .clip(RoundedCornerShape(radius))
                        .background(QuipColors.colorPrimary.copy(alpha = 0.15f))
                        .border(1.5.dp, QuipColors.colorPrimary, RoundedCornerShape(radius)),
                )
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "${radius.value.toInt()}dp",
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                )
                Text(
                    text = label.substringAfter("(").removeSuffix(")"),
                    style = MaterialTheme.typography.overline,
                    color = QuipColors.colorTextSecondary,
                )
            }
        }
    }
}

@Composable
private fun ComponentHeightsSection() {
    QuipSectionHeader(title = "Alturas de componentes")
    Spacer(Modifier.height(QuipDimens.padding8))
    val heights: List<Pair<String, Dp>> = listOf(
        "buttonHeight (56dp)"        to QuipDimens.buttonHeight,
        "dropdownItemHeight (48dp)"  to QuipDimens.dropdownItemHeight,
    )
    Column(verticalArrangement = Arrangement.spacedBy(QuipDimens.padding8)) {
        heights.forEach { (label, height) ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                    modifier = Modifier.width(200.dp),
                )
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(height * 0.5f)
                        .clip(RoundedCornerShape(QuipDimens.cornerRadius8))
                        .background(QuipColors.colorSecondary),
                )
            }
        }
    }
}

@Composable
private fun ElevationSection() {
    QuipSectionHeader(title = "Elevación (sombra)")
    Spacer(Modifier.height(QuipDimens.padding8))
    Row(horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding16)) {
        listOf(
            "Small\n${QuipDimens.elevationSmall.value.toInt()}dp" to QuipDimens.elevationSmall,
            "Medium\n${QuipDimens.elevationMedium.value.toInt()}dp" to QuipDimens.elevationMedium,
            "Large\n${QuipDimens.elevationLarge.value.toInt()}dp" to QuipDimens.elevationLarge,
        ).forEach { (label, elevation) ->
            androidx.compose.material.Surface(
                elevation = elevation,
                shape = RoundedCornerShape(QuipDimens.cornerRadius12),
                color = QuipColors.colorSurface,
                modifier = Modifier.weight(1f).height(56.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        text = label,
                        style = MaterialTheme.typography.caption,
                        color = QuipColors.colorText,
                    )
                }
            }
        }
    }
}
